/**
 * Shareable "I won" card.
 *
 * Generates a 1080x1080 PNG client-side on a hidden <canvas>, then
 * hands it to the platform Web Share API as a file so the user gets
 * a real image attachment (WhatsApp, Twitter, Telegram, IG Stories
 * all preview an attached image far better than they preview a link
 * preview built off og:image, especially in 1:1 personal chats).
 *
 * Falls back to:
 *   - clipboard copy of the share text + open Web Share with text-only
 *     when navigator.share doesn't accept files (older Android Chrome,
 *     all desktops).
 *   - last-resort: download the image and surface a toast asking the
 *     user to share it themselves.
 *
 * The card is intentionally self-contained — no external font load,
 * no remote images — so it renders instantly on a flaky network and
 * never blocks the share flow.
 */

import { toast } from '../components/toast.js';
import { formatKobo } from '../wallet.js';

const CARD_W = 1080;
const CARD_H = 1080;
const PALETTE = {
  bgTopLeft: '#5B2C91',     // brand violet (deep)
  bgBottomRight: '#E94BA2', // brand pink
  textInk: '#FFFFFF',
  textSoft: 'rgba(255,255,255,0.78)',
  textMuted: 'rgba(255,255,255,0.55)',
  gold: '#F7C667',
  goldDeep: '#E0A132',
  teal: '#5FE0BE',
  shadow: 'rgba(10,8,20,0.45)',
};

const TAGLINE = 'Won by skill — not luck.';
const SHARE_URL = 'https://triviana.xyz';

/**
 * Render the win card to a Blob (image/png). Pure — no DOM side
 * effects beyond a one-shot offscreen canvas the GC will reclaim.
 *
 * @param {object} args
 * @param {'live'|'duel'} args.kind   live trivia vs 1-v-1 duel
 * @param {number} args.amountKobo    winnings in kobo (can be 0 → "Winner!" headline)
 * @param {string} args.title         the trivia / duel headline (e.g. "Bible Trivia")
 * @param {string} [args.subtitle]    smaller secondary line (rank, opponent, etc.)
 */
export async function buildWinCardBlob({ kind, amountKobo, title, subtitle }) {
  const canvas = document.createElement('canvas');
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext('2d');

  _drawBackground(ctx);
  _drawWordmark(ctx);
  _drawWonChip(ctx);
  _drawAmount(ctx, amountKobo);
  _drawTitleBlock(ctx, kind, title, subtitle);
  _drawTagline(ctx);
  _drawUrl(ctx);

  return await new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png', 0.94);
  });
}

/**
 * End-to-end share flow: generate the image, try Web Share with files,
 * gracefully degrade. Always shows the user *something* — a toast
 * tells them whether they got the share sheet, a clipboard copy, or
 * a file download.
 */
export async function shareWin(args) {
  const { kind, amountKobo, title } = args;
  const shareText = _composeShareText(kind, amountKobo, title);
  let blob;
  try {
    blob = await buildWinCardBlob(args);
  } catch (err) {
    // Canvas failed (shouldn't, but just in case) — fall through to
    // text-only share.
    blob = null;
  }
  const file = blob
    ? new File([blob], 'triviana-win.png', { type: 'image/png' })
    : null;

  // Path A: image share via Web Share API. Most mobile browsers since
  // ~2022. canShare gates on file-type support — required because
  // some platforms accept share() but reject files: arrays.
  if (
    file
    && navigator.share
    && navigator.canShare
    && navigator.canShare({ files: [file] })
  ) {
    try {
      await navigator.share({
        files: [file],
        title: 'Triviana',
        text: shareText,
      });
      return;
    } catch (err) {
      // User cancelled — silent. Other errors fall through to the
      // next strategy so the user still gets to share something.
      if (err && err.name === 'AbortError') return;
    }
  }

  // Path B: text-only Web Share. Older Android Chrome / iOS Safari
  // with no file support but a working share sheet.
  if (navigator.share) {
    try {
      await navigator.share({
        title: 'Triviana',
        text: shareText,
        url: SHARE_URL,
      });
      return;
    } catch (err) {
      if (err && err.name === 'AbortError') return;
    }
  }

  // Path C: clipboard fallback — desktop browsers. Copy the text and
  // download the image side-by-side so the user can paste-and-attach.
  let copied = false;
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(`${shareText} ${SHARE_URL}`);
      copied = true;
    } catch {
      // ignore — fall through to download
    }
  }
  if (blob) {
    _downloadBlob(blob, 'triviana-win.png');
  }
  if (copied && blob) {
    toast.success('Copied caption + downloaded image. Paste into your post.');
  } else if (copied) {
    toast.success('Share text copied to clipboard.');
  } else if (blob) {
    toast.success('Image downloaded — share it on your favourite app.');
  } else {
    toast.error("Couldn't open share sheet — try again.");
  }
}

// ---- composition helpers ---------------------------------------------------

function _composeShareText(kind, amountKobo, title) {
  const amount = amountKobo > 0 ? formatKobo(amountKobo) : null;
  const event = kind === 'live' ? `live trivia (${title})` : `1-on-1 duel (${title})`;
  if (amount) {
    return `I just won ${amount} on a ${event} on Triviana. ${TAGLINE}`;
  }
  return `I just won a ${event} on Triviana. ${TAGLINE}`;
}

// ---- canvas drawing --------------------------------------------------------

function _drawBackground(ctx) {
  const grad = ctx.createLinearGradient(0, 0, CARD_W, CARD_H);
  grad.addColorStop(0, PALETTE.bgTopLeft);
  grad.addColorStop(1, PALETTE.bgBottomRight);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // Soft radial glow behind the amount so the gold text pops without a
  // hard background block. Centered slightly above middle so the layout
  // breathes.
  const glow = ctx.createRadialGradient(
    CARD_W / 2, CARD_H * 0.48,
    50,
    CARD_W / 2, CARD_H * 0.48,
    CARD_W * 0.55,
  );
  glow.addColorStop(0, 'rgba(247,198,103,0.22)');
  glow.addColorStop(1, 'rgba(247,198,103,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // Subtle decorative dots in the corners for texture.
  ctx.fillStyle = 'rgba(255,255,255,0.06)';
  for (const [cx, cy, r] of [
    [120, 130, 6],
    [180, 90, 4],
    [80, 200, 3],
    [CARD_W - 120, CARD_H - 130, 6],
    [CARD_W - 180, CARD_H - 90, 4],
    [CARD_W - 80, CARD_H - 200, 3],
  ]) {
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function _drawWordmark(ctx) {
  ctx.save();
  ctx.fillStyle = PALETTE.gold;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.font = '700 56px "Space Grotesk", "Inter", system-ui, sans-serif';
  // Letter-spacing approximation via per-glyph draw because canvas
  // doesn't honour `letter-spacing` in CSS terms.
  _drawSpacedText(ctx, 'TRIVIANA', CARD_W / 2, 90, 16);
  ctx.restore();
}

function _drawWonChip(ctx) {
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = '700 28px "Inter", system-ui, sans-serif';
  const label = 'YOU WON';
  const padX = 28;
  const w = ctx.measureText(label).width + padX * 2;
  const h = 60;
  const x = (CARD_W - w) / 2;
  const y = 250;
  // Pill background.
  ctx.fillStyle = 'rgba(255,255,255,0.12)';
  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.lineWidth = 2;
  _roundedRect(ctx, x, y, w, h, h / 2);
  ctx.fill();
  ctx.stroke();
  // Label.
  ctx.fillStyle = PALETTE.textInk;
  ctx.fillText(label, CARD_W / 2, y + h / 2 + 2);
  ctx.restore();
}

function _drawAmount(ctx, amountKobo) {
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const text = amountKobo > 0 ? formatKobo(amountKobo) : 'Winner!';
  // Auto-size: shrink to fit if the amount is wider than 80% of the
  // canvas. ₦100,000+ is the realistic upper bound; this lets a
  // hypothetical ₦999,999 still fit without overflow.
  let fontPx = 200;
  ctx.font = `800 ${fontPx}px "Space Grotesk", "Inter", system-ui, sans-serif`;
  while (ctx.measureText(text).width > CARD_W * 0.82 && fontPx > 80) {
    fontPx -= 8;
    ctx.font = `800 ${fontPx}px "Space Grotesk", "Inter", system-ui, sans-serif`;
  }
  // Drop shadow.
  ctx.fillStyle = PALETTE.shadow;
  ctx.fillText(text, CARD_W / 2 + 4, CARD_H / 2 + 6);
  // Gold gradient fill on the digits.
  const grad = ctx.createLinearGradient(0, CARD_H / 2 - fontPx / 2, 0, CARD_H / 2 + fontPx / 2);
  grad.addColorStop(0, '#FFE7A8');
  grad.addColorStop(0.55, PALETTE.gold);
  grad.addColorStop(1, PALETTE.goldDeep);
  ctx.fillStyle = grad;
  ctx.fillText(text, CARD_W / 2, CARD_H / 2);
  ctx.restore();
}

function _drawTitleBlock(ctx, kind, title, subtitle) {
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Kind label (small caps above title).
  ctx.font = '600 24px "Inter", system-ui, sans-serif';
  ctx.fillStyle = PALETTE.textMuted;
  const kindLabel = kind === 'live' ? 'LIVE TRIVIA' : '1-ON-1 DUEL';
  _drawSpacedText(ctx, kindLabel, CARD_W / 2, CARD_H / 2 + 150, 6);

  // Title — clamped width-wise; long titles wrap to two lines.
  if (title) {
    ctx.fillStyle = PALETTE.textInk;
    ctx.font = '700 40px "Inter", system-ui, sans-serif';
    _wrapAndDrawCentered(ctx, title, CARD_W / 2, CARD_H / 2 + 210, CARD_W * 0.84, 50, 2);
  }
  if (subtitle) {
    ctx.fillStyle = PALETTE.textSoft;
    ctx.font = '500 26px "Inter", system-ui, sans-serif';
    ctx.fillText(subtitle, CARD_W / 2, CARD_H / 2 + 285);
  }
  ctx.restore();
}

function _drawTagline(ctx) {
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = PALETTE.teal;
  ctx.font = 'italic 600 36px "Inter", "Georgia", serif';
  ctx.fillText(TAGLINE, CARD_W / 2, CARD_H - 140);
  ctx.restore();
}

function _drawUrl(ctx) {
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = PALETTE.textSoft;
  ctx.font = '600 26px "Space Grotesk", "Inter", system-ui, sans-serif';
  _drawSpacedText(ctx, 'triviana.xyz', CARD_W / 2, CARD_H - 78, 2);
  ctx.restore();
}

// ---- canvas micro-helpers --------------------------------------------------

function _roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function _drawSpacedText(ctx, text, cx, cy, letterSpacing) {
  // Sum measured width + (n-1)*letterSpacing, then draw glyph-by-glyph
  // centered around cx.
  let total = 0;
  const widths = [];
  for (const ch of text) {
    const w = ctx.measureText(ch).width;
    widths.push(w);
    total += w + letterSpacing;
  }
  total -= letterSpacing; // no trailing space
  let x = cx - total / 2;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const w = widths[i];
    ctx.fillText(ch, x + w / 2, cy);
    x += w + letterSpacing;
  }
}

function _wrapAndDrawCentered(ctx, text, cx, startY, maxWidth, lineHeight, maxLines) {
  const words = text.split(/\s+/);
  const lines = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && current) {
      lines.push(current);
      current = word;
      if (lines.length === maxLines - 1) break;
    } else {
      current = candidate;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  // Truncate with ellipsis if we ran out of room.
  if (lines.length === maxLines && words.length > 0) {
    let last = lines[lines.length - 1];
    while (ctx.measureText(`${last}…`).width > maxWidth && last.length > 0) {
      last = last.slice(0, -1);
    }
    if (last !== lines[lines.length - 1]) lines[lines.length - 1] = `${last}…`;
  }
  const totalHeight = lineHeight * lines.length;
  let y = startY - totalHeight / 2 + lineHeight / 2;
  for (const line of lines) {
    ctx.fillText(line, cx, y);
    y += lineHeight;
  }
}

function _downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the download a tick to start before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
